import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import {
    RbpCloudSubFunctionAuxiliaryModeContext,
    type RbpCloudSubFunctionAuxiliaryModeContextValue,
} from './rbp-cloud-sub-function-auxiliary-mode-context';
import Add from '@mui/icons-material/Add';
import Save from '@mui/icons-material/Save';
import Table from '../../component/table/table';
import { MUIDataTableColumnDef, MUIDataTableCustomHeadRenderer } from 'mui-datatables';

import _ from 'lodash';
import './scss/call-service.page.scss';
import { apiManage_rbp } from '../../request';
import setLoading from '../../component/linear-determinate';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import DialogComponent from '../../component/dialog/dialog.component';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import GlobalNotification from '../../component/common/snackbar';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import i18n from '../../i18n';
import CommonInput from '../../component/form/common-input.component';
import { CallServicePageSkeleton } from './sub-function-settings-skeletons.component';

/** 列表区（Figma 1901:25388）：表格仅展示常用语种列 */
const CALL_SERVICE_COMMON_TITLE_KEYS = ['title_cn', 'title_en', 'title_it', 'title_es'] as const;

/**
 * 呼叫服务编辑弹窗（与列表「更多设置」同一套 UI）：可编辑全部语种标题（`sm+` 下每行 3 列）。
 */
const CALL_SERVICE_MORE_SETTINGS_TITLE_KEYS = [
    'title_cn',
    'title_en',
    'title_it',
    'title_es',
    'title_fr',
    'title_de',
    'title_pt',
    'title_sl',
    'title_ca',
    'title_ja',
    'title_ko',
] as const;

/** 表格列头简写（与稿面 CN/EN/… 一致） */
const CALL_SERVICE_TITLE_TABLE_HEAD: Record<(typeof CALL_SERVICE_COMMON_TITLE_KEYS)[number], string> = {
    title_cn: 'CN',
    title_en: 'EN',
    title_it: 'IT',
    title_es: 'ES',
};

/** 各 title 字段对应 i18n key（表单 label） */
const CALL_SERVICE_TITLE_LABEL_I18N: Record<string, string> = {
    title_cn: 'call_service_setting_page_title_cn',
    title_en: 'call_service_setting_page_title_en',
    title_it: 'call_service_setting_page_title_it',
    title_es: 'call_service_setting_page_title_es',
    title_fr: 'call_service_setting_page_title_fr',
    title_de: 'call_service_setting_page_title_de',
    title_pt: 'call_service_setting_page_title_pt',
    title_sl: 'call_service_setting_page_title_sl',
    title_ca: 'call_service_setting_page_title_ca',
    title_ja: 'call_service_setting_page_title_ja',
    title_ko: 'call_service_setting_page_title_ko',
};

/**
 * 云 RBP「子功能管理」— 呼叫服务设置：列表与多语言标题维护，请求走 `apiManage_rbp`。
 * 隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class CallServicePage extends React.Component<Props, State> {
    /** 读取顶栏「辅助模式」开关（与自助点餐等子页一致，避免双 contextType） */
    public static contextType = RbpCloudSubFunctionAuxiliaryModeContext;

    /**
     * React 注入的辅助模式上下文。
     */
    private get auxiliaryPageContext(): RbpCloudSubFunctionAuxiliaryModeContextValue {
        return (this as unknown as { context: RbpCloudSubFunctionAuxiliaryModeContextValue }).context;
    }

    /** 表格无数据时的占位空数组，避免 Table 收到 undefined */
    public emptyArr = [];
    /** 预留：多语言标题非空校验字段列表（与 RBP2 对齐；当前仅强制英文） */
    public checkType = [
        'title_cn',
        'title_en',
        'title_it',
        'title_es',
        'title_fr',
        'title_de',
        'title_pt',
        'title_sl',
        'title_ca',
        'title_ja',
        'title_ko',
    ];

    public state: State = {
        /** 首包列表未返回前展示骨架 */
        initialListReady: false,
        tables: [],
        detail: {} as CallServiceContent,
        deleteOpen: false,
        selectedIds: [],
        /** 新增 / 行编辑 / 列表「更多设置」共用同一弹窗（更多设置样式） */
        extendedLanguagesOpen: false,
        /** 弹窗「保存更改」提交中，与 `LoadingOverlayButton` 同步 */
        saveSubmitting: false,
        /** 弹窗顶栏主标题：在打开时快照，避免编辑中文/英文标题时顶栏随输入变化 */
        moreSettingsDialogHeaderTitle: '',
    };

    /**
     * 进入页签后拉取呼叫服务列表（子功能壳页 `HookDecorator` 已保证隧道与当前餐厅就绪）。
     */
    public componentDidMount(): void {
        void this.init();
    }

    /**
     * 请求呼叫服务列表并写入 state。
     */
    init = async () => {
        try {
            const result = await apiManage_rbp.get_call_service();
            if (!_.get(result, 'error')) {
                this.setState({
                    tables: result,
                });
            } else {
                GlobalNotification.error(i18n.t('call_service_setting_page_get_call_service_failed'));
            }
        } catch (error) {
            // 列表失败已由通知提示，此处不重复抛错
        } finally {
            this.setState({ initialListReady: true });
        }
    };

    /**
     * 根据打开弹窗时的行数据计算顶栏主标题（CN → EN → 通用 fallback），与表单编辑过程解耦。
     */
    private getMoreSettingsDialogHeaderTitleSnapshot(row: CallServiceContent): string {
        const cn = String(row?.title_cn ?? '').trim();
        const en = String(row?.title_en ?? '').trim();
        return cn || en || i18n.t('call_service_setting_page_more_settings_dialog_fallback_title');
    }

    /**
     * 打开新增：清空草稿并打开与「更多设置」相同的编辑弹窗。
     */
    openAddForm = (): void => {
        this.setState({
            extendedLanguagesOpen: true,
            detail: {} as CallServiceContent,
            moreSettingsDialogHeaderTitle: this.getMoreSettingsDialogHeaderTitleSnapshot(
                {} as CallServiceContent,
            ),
        });
    };

    /**
     * 更新当前编辑行某一字段的值。
     * @param value - 输入值
     * @param key - 字段名（如 title_en）
     */
    onChange = (value: number | string | string[], key: string) => {
        const { detail } = this.state;
        const newValue = value;
        _.set(detail, key, newValue);
        this.setState({
            detail,
        });
    };

    /**
     * 保存前校验：英文标题必填。
     * @returns 是否校验失败
     */
    checkEmpty = (): boolean => {
        const { detail } = this.state;
        let isError = false;
        const regular = BASE_CHECK_TYPE?.emptyString?.regular;
        if (!detail?.title_en || regular.test(detail?.title_en.trim())) {
            GlobalNotification.error(i18n.t('call_service_setting_page_english_title_can_not_empty'));
            isError = true;
        }
        return isError;
    };

    /**
     * 新增 / 修改一个呼叫服务
     */
    saveCallService = async () => {
        try {
            this.setState({ saveSubmitting: true });
            setLoading(true);
            if (_.get(this.state.detail, 'id', '') !== '') {
                const isError = this.checkEmpty();
                if (isError) return;
                const result = await apiManage_rbp.patch_call_service(this.state.detail);
                if (!result.error) {
                    GlobalNotification.success(i18n.t('google_text_update_successfully'));
                    this.setState(
                        {
                            detail: {} as CallServiceContent,
                            extendedLanguagesOpen: false,
                            moreSettingsDialogHeaderTitle: '',
                        },
                        () => {
                            void this.init();
                        },
                    );
                } else {
                    GlobalNotification.error(i18n.t('google_text_verify_failed'));
                }
            } else {
                const isError = this.checkEmpty();
                if (isError) return;
                const result = await apiManage_rbp.post_call_service(this.state.detail);
                if (!_.get(result, 'error')) {
                    GlobalNotification.success(i18n.t('google_text_add_succeed'));
                    this.setState(
                        {
                            detail: {} as CallServiceContent,
                            extendedLanguagesOpen: false,
                            moreSettingsDialogHeaderTitle: '',
                        },
                        () => {
                            void this.init();
                        },
                    );
                } else {
                    GlobalNotification.error(i18n.t('google_text_add_failed'));
                }
            }
        } catch (error) {
            // 错误由通知与 finally 处理
        } finally {
            setLoading(false);
            this.setState({ saveSubmitting: false });
        }
    };

    /**
     * 批量删除选中的呼叫服务项。
     */
    handleDeleteDishes = async () => {
        try {
            setLoading(true);
            const result = await apiManage_rbp.patch_del_cell_service(this.state.selectedIds);
            if (!result.error) {
                GlobalNotification.success(i18n.t('google_text_delete_success'));
                void this.init();
            } else {
                GlobalNotification.error(i18n.t('google_text_delete_failed'));
            }
        } catch (error) {
            // 同上
        } finally {
            setLoading(false);
        }
    };

    /**
     * 表格删除工具栏回调：记录选中 id 并打开确认框。
     */
    onDeleteSelectedIds = (
        rowsDeleted: {
            lookup: { [dataIndex: number]: boolean };
            data: Array<{ index: number; dataIndex: number }>;
        },
    ) => {
        const selectedIds = rowsDeleted?.data.map((item) => this.state.tables?.[item?.dataIndex]?.id);
        this.setState({
            deleteOpen: true,
            selectedIds,
        });
    };

    /**
     * 标题字段在表单中的 label 文案。
     */
    private titleFieldFormLabel(key: string): string {
        const k = CALL_SERVICE_TITLE_LABEL_I18N[key];
        return k ? i18n.t(k) : key;
    }

    /**
     * 编辑弹窗顶栏：主标题为打开时快照的展示名 + 副标题 ID（对齐 Figma 3691:41597）；新增无 id 时不展示 ID 行。
     */
    private renderMoreSettingsDialogTitleContent(
        displayTitle: string,
        detail: CallServiceContent,
    ): React.ReactNode {
        const idVal = _.get(detail, 'id') as CallServiceContent['id'] | undefined;
        const hasId =
            idVal !== undefined &&
            idVal !== null &&
            String(idVal) !== '' &&
            !(typeof idVal === 'number' && Number.isNaN(idVal));
        return (
            <Box
                className="call-service-page__more-settings-header-titles"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}
            >
                <Typography
                    component="div"
                    sx={{
                        fontWeight: 700,
                        fontSize: 20,
                        lineHeight: '24px',
                        color: 'primary.main',
                    }}
                >
                    {displayTitle}
                </Typography>
                {hasId ? (
                    <Typography
                        component="div"
                        sx={{
                            fontWeight: 500,
                            fontSize: 16,
                            lineHeight: '24px',
                            color: 'rgba(31, 41, 64, 0.5)',
                            mt: 0,
                        }}
                    >
                        {`ID: ${String(idVal)}`}
                    </Typography>
                ) : null}
            </Box>
        );
    }

    /**
     * 点击数据行（非复选框、非「更多设置」）打开与「更多设置」相同的编辑弹窗。
     */
    handleTableCellClick = (
        _colData: unknown,
        cellMeta: { colIndex: number; rowIndex: number; dataIndex: number; event: React.MouseEvent },
    ): void => {
        const el = cellMeta.event.target as HTMLElement;
        if (el.closest('.MuiCheckbox-root')) return;
        if (el.closest('.call-service-page__more-settings-btn')) return;
        const row = this.state.tables[cellMeta.dataIndex];
        if (!row) return;
        this.setState({
            detail: _.cloneDeep(row) as CallServiceContent,
            extendedLanguagesOpen: true,
            moreSettingsDialogHeaderTitle: this.getMoreSettingsDialogHeaderTitleSnapshot(row),
        });
    };

    /**
     * 从列表「更多设置」打开同一编辑弹窗（与行点击、新增共用组件与保存逻辑）。
     */
    openExtendedLanguagesFromRow = (dataIndex: number): void => {
        const row = this.state.tables[dataIndex];
        if (!row) return;
        this.setState({
            extendedLanguagesOpen: true,
            detail: _.cloneDeep(row) as CallServiceContent,
            moreSettingsDialogHeaderTitle: this.getMoreSettingsDialogHeaderTitleSnapshot(row),
        });
    };

    /**
     * 关闭编辑弹窗：仅重置本地弹窗状态，不请求列表（保存成功时由 `saveCallService` 内 `init` 刷新）。
     */
    closeExtendedLanguagesDialog = (): void => {
        this.setState({
            extendedLanguagesOpen: false,
            detail: {} as CallServiceContent,
            moreSettingsDialogHeaderTitle: '',
        });
    };

    /**
     * 构建呼叫服务表格列；辅助模式开启时 IT 列表头下展示设计稿副文案。
     * @param auxiliaryModeEnabled - 顶栏「辅助模式」是否开启
     */
    private buildCallServiceColumns(auxiliaryModeEnabled: boolean): MUIDataTableColumnDef[] {
        const langCols: MUIDataTableColumnDef[] = CALL_SERVICE_COMMON_TITLE_KEYS.map((field) => {
            const short = CALL_SERVICE_TITLE_TABLE_HEAD[field];
            if (field === 'title_it') {
                return {
                    name: field,
                    label: short,
                    options: {
                        sort: false,
                        ...(auxiliaryModeEnabled
                            ? {
                                  customHeadRender: (columnMeta: MUIDataTableCustomHeadRenderer) => (
                                      <th
                                          key={columnMeta.name}
                                          className="MuiTableCell-root MuiTableCell-head"
                                          scope="col"
                                      >
                                          <Box sx={{ lineHeight: 1.25, py: 0.5, pr: 1 }}>
                                              <Typography component="div" variant="body2" sx={{ fontWeight: 600 }}>
                                                  IT
                                              </Typography>
                                              <SubFunctionAuxiliaryHint
                                                  enabled
                                                  i18nKey="call_service_page_aux_it_column_sub"
                                                  className="call-service-page__it-col-aux"
                                              />
                                          </Box>
                                      </th>
                                  ),
                              }
                            : {}),
                    },
                };
            }
            return {
                name: field,
                label: short,
                options: { sort: false },
            };
        });

        return [
            {
                name: 'id',
                label: 'ID',
                options: {
                    sort: false,
                },
            },
            ...langCols,
            {
                name: '_otherLanguages',
                label: i18n.t('call_service_setting_page_other_languages_column'),
                options: {
                    sort: false,
                    /** 与设计稿列对齐：「更多设置」居中（Figma 3684:41292） */
                    setCellProps: () => ({
                        className: 'call-service-page__more-settings-cell',
                    }),
                    customBodyRenderLite: (dataIndex: number) => (
                        <Button
                            type="button"
                            variant="text"
                            color="primary"
                            disableElevation
                            disableRipple
                            className="call-service-page__more-settings-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                this.openExtendedLanguagesFromRow(dataIndex);
                            }}
                        >
                            {i18n.t('call_service_setting_page_more_settings')}
                        </Button>
                    ),
                },
            },
        ];
    }

    render() {
        if (!this.state.initialListReady) {
            return <CallServicePageSkeleton />;
        }
        const { tables, detail, deleteOpen, extendedLanguagesOpen, saveSubmitting, moreSettingsDialogHeaderTitle } =
            this.state;
        /** 为「其他语言设置」列提供占位字段，满足 mui-datatables 行数据访问 */
        const tableData = (tables || this.emptyArr).map((row: CallServiceContent) => ({
            ...row,
            _otherLanguages: '',
        }));
        /** 顶栏「辅助模式」：控制页头说明与 IT 列副文案 */
        const auxiliaryModeEnabled = this.auxiliaryPageContext?.auxiliaryModeEnabled ?? false;
        /** 表格列（随辅助模式重建，保证表头副文案同步） */
        const callServiceColumns = this.buildCallServiceColumns(auxiliaryModeEnabled);
        return (
            <Box className="call-service-page">
                <Box className="call-service-page__surface">
                    <Box className="call-service-page__header">
                        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                            <Typography variant="h4" color="primary" className="call-service-page__page-title">
                                {i18n.t('call_service_setting_page_heading')}
                            </Typography>
                            <SubFunctionAuxiliaryHint
                                enabled={auxiliaryModeEnabled}
                                i18nKey="call_service_page_aux_page_intro"
                            />
                        </Box>
                    </Box>

                    {/* 列表区（Figma 1901:25388）：表格无分页、无外层白底；「新增」在数据底部 */}
                    <Box className="call-service-page__table-card">
                        <Box className="call-service-page__datatable">
                            <Table
                                data={tableData}
                                columns={callServiceColumns}
                                selectableRowsHideCheckboxes={false}
                                pagination={false}
                                selectableRowsHeader={true}
                                selectToolbarPlacement="replace"
                                elevation={0}
                                onCellClick={this.handleTableCellClick}
                                setRowProps={() => ({
                                    className: 'call-service-page__table-row--clickable',
                                })}
                                otherOption={{
                                    onRowsDelete: this.onDeleteSelectedIds,
                                }}
                            />
                        </Box>
                        {/**
                         * 列表底部「新增」：Figma 3691:41389 — 白底圆角 16、紫字加粗 24 + 32 图标，与数据行同宽居中
                         */}
                        <Box className="call-service-page__table-footer">
                            <Box
                                component="button"
                                type="button"
                                className="call-service-page__add-row-btn"
                                onClick={this.openAddForm}
                            >
                                <Typography component="span" variant="inherit" className="call-service-page__add-row-label">
                                    {i18n.t('call_service_setting_page_add_call_service_row')}
                                </Typography>
                                <Add className="call-service-page__add-row-icon" aria-hidden />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <DialogComponent
                    open={extendedLanguagesOpen}
                    fullWidth={false}
                    maxWidth={false}
                    className="call-service-page__more-settings-dialog"
                    onClose={this.closeExtendedLanguagesDialog}
                    sx={{
                        /** 覆盖公共 Dialog 默认紫底纸面，对齐 Figma 3691:41596 白底弹窗 */
                        '& .MuiPaper-root.call-service-page__more-settings-dialog-paper': {
                            background: 'var(--fill-neutral-100, #fff)',
                            backgroundImage: 'none',
                            border: '1px solid rgba(31, 41, 64, 0.12)',
                        },
                    }}
                    PaperProps={{
                        className: 'call-service-page__more-settings-dialog-paper',
                        sx: {
                            width: 768,
                            maxWidth: 'calc(100vw - 48px)',
                            borderRadius: '16px',
                            boxSizing: 'border-box',
                        },
                    }}
                    defaultHeaderProps={{
                        titleContent: this.renderMoreSettingsDialogTitleContent(
                            moreSettingsDialogHeaderTitle,
                            detail,
                        ),
                        onClose: this.closeExtendedLanguagesDialog,
                    }}
                    dialogContentStyle={{
                        paddingTop: 24,
                        paddingLeft: 16,
                        paddingRight: 16,
                        paddingBottom: 0,
                    }}
                    footer={
                        <Box className="call-service-page__more-settings-footer" sx={{ width: '100%', px: 2, pb: 3 }}>
                            <LoadingOverlayButton
                                type="button"
                                color="primary"
                                variant="contained"
                                fullWidth
                                disableElevation
                                loading={saveSubmitting}
                                loadingIndicatorSize={24}
                                onClick={() => void this.saveCallService()}
                                startIcon={<Save sx={{ fontSize: 24 }} aria-hidden />}
                                sx={{
                                    borderRadius: '8px',
                                    height: 56,
                                    textTransform: 'none',
                                    fontSize: 20,
                                    fontWeight: 500,
                                    lineHeight: '24px',
                                    boxShadow: 'none',
                                    gap: 1,
                                    '&:hover': { boxShadow: 'none' },
                                }}
                            >
                                {i18n.t('call_service_setting_page_save_changes')}
                            </LoadingOverlayButton>
                        </Box>
                    }
                >
                    <Box
                        className="call-service-page__more-settings-form"
                        sx={{
                            /** 一行 3 语种：`minmax(0,1fr)` 防止长标签撑破列宽 */
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                            columnGap: 1,
                            rowGap: 2,
                            width: '100%',
                            pb: 3,
                        }}
                    >
                        {CALL_SERVICE_MORE_SETTINGS_TITLE_KEYS.map((key) => (
                            <Box
                                key={key}
                                className="call-service-page__more-settings-field"
                                sx={{
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <CommonInput
                                    inputType="INPUT"
                                    variant="outlined"
                                    fullWidth
                                    label={this.titleFieldFormLabel(key)}
                                    value={_.get(detail, key) ?? ''}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        this.onChange(event.target.value, key);
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </DialogComponent>

                {deleteOpen && (
                    <AlertDialog
                        open={true}
                        variant="delete"
                        title={i18n.t('call_service_setting_page_delete_call_service')}
                        onCancel={() => {
                            this.setState({
                                deleteOpen: false,
                            });
                        }}
                        onConfirm={() => {
                            this.setState(
                                {
                                    deleteOpen: false,
                                },
                                () => {
                                    void this.handleDeleteDishes();
                                },
                            );
                        }}
                    />
                )}
            </Box>
        );
    }
}

export default CallServicePage;

type State = {
    /** 首屏加载中（预留，当前与 setLoading 并行） */
    /** 首包列表已返回，结束骨架 */
    initialListReady: boolean;
    /** 呼叫服务列表数据 */
    tables: Array<CallServiceContent>;
    /** 当前编辑/新增草稿（与弹窗受控表单绑定） */
    detail: CallServiceContent;
    /** 删除确认弹窗是否打开 */
    deleteOpen: boolean;
    /** 待批量删除的 id 列表 */
    selectedIds: number[];
    /** 新增 / 行编辑 / 列表「更多设置」共用的编辑弹窗（更多设置样式，Figma 3691:41596） */
    extendedLanguagesOpen: boolean;
    /** 弹窗主按钮提交中（`LoadingOverlayButton`） */
    saveSubmitting: boolean;
    /** 编辑弹窗顶栏主标题快照：打开时写入，编辑表单时不随 `detail.title_*` 变化 */
    moreSettingsDialogHeaderTitle: string;
};

type Props = Record<string, never>;

/** 呼叫服务项：与 RBP2 / 后端及 `apiManage_rbp` 读写结构一致 */
export type CallServiceContent = {
    /** 记录 id，新增时为空 */
    id: number;
    title_cn: string;
    title_en: string;
    title_it: string;
    title_es: string;
    title_fr: string;
    title_de: string;
    title_pt: string;
    title_sl: string;
    title_ca: string;
    title_ja: string;
    title_ko: string;
    value_cn: string;
    value_en: string;
    value_it: string;
    value_es: string;
    value_fr: string;
    value_de: string;
    value_pt: string;
    value_sl: string;
    value_ca: string;
    value_ja: string;
    value_ko: string;
};
